import * as React from 'react';
import { useMutation } from '@apollo/client';
import { Slider, Button, Card, Typography, Space, message, InputNumber } from 'antd';
import { BulbOutlined, CheckOutlined, ClockCircleOutlined } from '@ant-design/icons';
import { FORCE_DIM_SINGLE, TForceDimSingleInput, TForceDimSingleOutput } from '../../../../../graphql/schema';
import { INodeInfo } from '../../index';

const { Title, Text } = Typography;

interface IControlProps {
  selectedNode: INodeInfo | null;
}

const Control: React.FunctionComponent<IControlProps> = ({ selectedNode }) => {
  const [dimLevel, setDimLevel] = React.useState<number>(100);
  const [duration, setDuration] = React.useState<number | null>(30);
  const [isLoading, setIsLoading] = React.useState<boolean>(false);

  const [forceDimSingle] = useMutation<TForceDimSingleOutput, { input: TForceDimSingleInput }>(
    FORCE_DIM_SINGLE
  );

  const handleDimLevelChange = (value: number) => {
    setDimLevel(value);
  };

  const handleDurationChange = (value: number | null) => {
    setDuration(value || null);
  };

  const handleConfirmDim = async () => {
    if (!selectedNode?.nodes.id) {
      message.error("Không tìm thấy thông tin node!");
      return;
    } 

    if (!duration) {
      message.error("Vui lòng điều chỉnh độ sáng và thời gian!");
      return;
    }

    setIsLoading(true);
    
    try {
      await forceDimSingle({
        variables: {
          input: {
            nodeId: selectedNode.nodes.id,
            dimValue: dimLevel,
            duration: duration
          }
        }
      });

      const durationText = duration ? ` trong ${duration} giây` : ' vĩnh viễn';
      message.success(`Đã điều chỉnh độ sáng đèn xuống ${dimLevel}%${durationText} thành công!`);
    } catch (error: any) {
      message.error(`Lỗi điều chỉnh độ sáng: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const getDimLevelColor = (level: number) => {
    if (level === 0) return '#722ed1'; // Purple for off (doesn't look disabled)
    if (level >= 80) return '#52c41a'; // Green for bright
    if (level >= 50) return '#faad14'; // Orange for medium
    if (level >= 20) return '#fa8c16'; // Dark orange for dim
    return '#f5222d'; // Red for very dim
  };

  const getDimLevelText = (level: number) => {
    if (level === 0) return 'Tắt đèn';
    if (level >= 80) return 'Rất sáng';
    if (level >= 50) return 'Sáng';
    if (level >= 20) return 'Mờ';
    return 'Rất mờ';
  };

  if (!selectedNode) {
    return (
      <div className="p-4">
        <p className="text-gray-500">Vui lòng chọn một node để điều khiển.</p>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto">
      <Card 
        title={
          <Space>
            <BulbOutlined style={{ color: getDimLevelColor(dimLevel) }} />
            <Title level={4} style={{ margin: 0 }}>Điều khiển độ sáng đèn</Title>
          </Space>
        }
        className="shadow-lg"
      >
        <div className="space-y-6">

          {/* Current Status */}
          <div className="text-center bg-gray-50 rounded-lg pt-3">
            <Text strong className="text-lg">
              Độ sáng hiện tại: {dimLevel}%
            </Text>
            <div className="mt-2">
              <Text type="secondary">
                {getDimLevelText(dimLevel)}
              </Text>
            </div>
          </div>

          {/* Slider */}
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <Text strong>Điều chỉnh độ sáng:</Text>
              <Text type="secondary">0% - 100%</Text>
            </div>
            
            <Slider
              min={0}
              max={100}
              value={dimLevel}
              onChange={handleDimLevelChange}
              marks={{
                0: '0%',
                25: '25%',
                50: '50%',
                75: '75%',
                100: '100%'
              }}
              tooltip={{
                formatter: (value) => `${value}%`
              }}
              trackStyle={{
                backgroundColor: getDimLevelColor(dimLevel),
                height: 8
              }}
              handleStyle={{
                borderColor: getDimLevelColor(dimLevel),
                height: 24,
                width: 8,
                marginTop: -8,
                borderRadius: 2,
                backgroundColor: getDimLevelColor(dimLevel)
              }}
              railStyle={{
                height: 8
              }}
            />
          </div>

          {/* Duration Control */}
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <Space>
                <ClockCircleOutlined />
                <Text strong>Thời gian điều khiển:</Text>
              </Space>
              <Text type="secondary">Tùy chọn</Text>
            </div>
            
            <InputNumber
              placeholder="Nhập thời gian (giây) - để trống để dim vĩnh viễn"
              min={1}
              max={86400} // 24 hours in seconds
              value={duration}
              onChange={handleDurationChange}
              className="w-full"
              addonAfter="giây"
              size="large"
            />
            
            {/* <div className="text-center">
              <Text type="secondary" className="text-sm">
                {duration ? `Sẽ dim trong ${duration} giây` : 'Sẽ dim trong 30 giây'}
              </Text>
            </div> */}
          </div>

          {/* Visual Indicator */}
          {/* <div className="flex justify-center">
            <div 
              className="w-16 h-16 rounded-full flex items-center justify-center transition-all duration-300"
              style={{
                backgroundColor: getDimLevelColor(dimLevel),
                opacity: dimLevel / 100 + 0.1,
                boxShadow: `0 0 20px ${getDimLevelColor(dimLevel)}40`
              }}
            >
              <BulbOutlined style={{ fontSize: '24px', color: 'white' }} />
            </div>
          </div> */}

          {/* Confirm Button */}
          <div className="flex justify-center">
            <Button
              type="primary"
              size="large"
              icon={<CheckOutlined />}
              onClick={handleConfirmDim}
              loading={isLoading}
              className="h-12 px-8 text-base font-medium"
              style={{
                backgroundColor: getDimLevelColor(dimLevel),
                borderColor: getDimLevelColor(dimLevel)
              }}
            >
              {dimLevel === 0 ? 'Tắt đèn' : 'Xác nhận điều chỉnh độ sáng'}
            </Button>
          </div>

          {/* Instructions */}
          <div className="text-center bg-yellow-50 rounded-lg p-3">
            <Text type="secondary" className="text-sm">
              💡 Kéo thanh trượt để điều chỉnh độ sáng từ 0% (tắt) đến 100% (sáng nhất). 
              Nhập thời gian để dim tạm thời hoặc để trống để dim vĩnh viễn. 
              Sau đó nhấn nút xác nhận để áp dụng thay đổi.
            </Text>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default Control;
